import { Component, OnInit, ElementRef, ViewChild, Inject, OnDestroy, AfterViewInit } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { AuthenticationService, SocialLoginProvider } from '../auth.service';
import { Router, ActivatedRoute } from '@angular/router';
import { environment } from '../../../environments/environment';
import { ForgottenPasswordComponent } from './forgotten-password/forgotten-password.component';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    standalone: false
})
export class LoginComponent implements OnInit, AfterViewInit {
  loginError: string;
  loginErrorMessage: string;
  email = new FormControl('');
  password = new FormControl('');
  twoFactorCode = new FormControl('');
  twoFactorEntry: boolean = false;
  @ViewChild("twoFactorCodeElement") twoFactorElement: ElementRef<HTMLInputElement>
  redirectUrl: string;

  socialLoginProviders: SocialLoginProvider[] = [];
  loadingSocialLoginProviders = false;

  busy: boolean = false;
  showMuwDisclaimer = !!environment['showMuwStrings'];
  enableEmailLogin = environment.enableEmailLogin;

  @ViewChild('emailField', { static: true })
  emailField: ElementRef<HTMLInputElement>;
  autofilled = false;

  @ViewChild('passwordInput', { static: false })
  private passwordInput?: ElementRef<HTMLInputElement>;

  @ViewChild('forgottenPasswordPopup', { static: true })
  forgottenPasswordPopup: ForgottenPasswordComponent;

  showPassword = false;

  constructor(private authenticationService: AuthenticationService, private router: Router, private route: ActivatedRoute, @Inject('Window') private window: Window) {
    
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      try {
        if (this.emailField.nativeElement.matches(":-webkit-autofill"))
          this.autofilled = true;
      } catch (err) {

        try {
          if (this.emailField.nativeElement.matches(":autofill"))
            this.autofilled = true;
        } catch (err) {
        }
      }
    }, 250);
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.redirectUrl = params['redirectUrl'];

      this.busy = true;
      if (params['adoptToken']) {
        // Originally used by the Report Viewer to pass its token and log in the user in the browser; now only for trouble shooting
        localStorage.setItem('refreshToken', params['adoptToken']);
        this.authenticationService.adoptToken().subscribe(result => {
          this.busy = false;
          if (result.success) {
            this.redirectToTarget();
          } else {
            this.window.alert("Cannot log you in, the token is invalid.");
          }
        });
      } else if (params['loginErrorMessage']) {
        this.loginErrorMessage = params['loginErrorMessage'];
        this.busy = false;
        this.showMuwDisclaimer = false;
      } else {
        // This scenario works out when the user presses Reload while the last token is still valid
        this.authenticationService.reviveToken().subscribe(result => {
          this.busy = false;
          if (result.success) {
            this.redirectToTarget();
          }
        });
      }
    });

    this.loadingSocialLoginProviders = true;
    this.authenticationService.getSocialLoginProviders().subscribe(providers => {
      this.socialLoginProviders = providers;
      this.loadingSocialLoginProviders = false;
    });
  }

  login() {
    this.busy = true;
    this.loginError = null;

    let tfCode;

    if (this.twoFactorEntry)
      tfCode = this.twoFactorCode.value;

    this.authenticationService.authenticate(this.email.value, this.password.value, tfCode)
    .subscribe(result => {
      this.busy = false;

      if (result.success) {
        this.redirectToTarget();
      } else if (result.twoFactorNeeded) {
        this.twoFactorEntry = true;
        window.setTimeout(() => this.twoFactorElement.nativeElement.focus(), 200);
      } else {
        this.loginError = result.message;
      }
    })
  }

  private redirectToTarget() {
    let url = this.redirectUrl || '/';
    this.router.navigateByUrl(url);
  }

  redirectToSocialLogin(provider: SocialLoginProvider) {
    this.authenticationService.getFrontendConfig().then(config => {
      let authUrl = config.authServiceUrl + '/api/v1';

      if (authUrl.startsWith('/') && window.location.hostname === "localhost")
        authUrl = "https://ai-dev.dedicaid.com/auth/api/v1";

      let url = authUrl + provider.url;
      if (this.redirectUrl)
        url += '?redirectUrl=' + encodeURIComponent(this.redirectUrl);

      this.window.location.href = url;
    });
  }

  onForgottenPassword() {
    console.log("onForgottten")
    this.forgottenPasswordPopup.email = this.email.value;
    this.forgottenPasswordPopup.open();
  }

  continue() {
    this.busy = true;
    this.authenticationService.continueAuthentication(this.email.value).subscribe({
      next: res => {

        if (!res) {
          this.busy = false;
          this.loginError = $localize `This user is not registered.`;
          return;
        }

        if (res.continue === "password") {
          this.busy = false;
          this.showPassword = true;
          setTimeout(() => this.passwordInput?.nativeElement.focus(), 100);
        } else if (res.continue === "sso") {
          // Redirect to SSO
          this.authenticationService.getFrontendConfig().then(config => {
            this.window.location = config.authServiceUrl + '/api/v1/tenancy/sso/initiate/' +
              encodeURIComponent(this.email.value) + '?redirectUrl=' + encodeURIComponent(this.redirectUrl ?? '');
          });
        } else {
          // Redirect to OAuth
          const provider = this.socialLoginProviders.find(prov => prov.id === res.continue);
          if (provider)
            this.redirectToSocialLogin(provider);
          else {
            this.busy = false;
            this.loginError = $localize `Account uses an unsupported 3rd party provider`;
          }
        }
      },
      error: err => {
        this.busy = false;
        this.loginError = err?.message;
      },
    });
  }

  continueOrLogin() {
    if (this.showPassword)
      this.login();
    else
      this.continue();
  }

}
